/**
 * The projects of one client as editable lines, and the changes between what was loaded and what is on screen.
 * Pure: no network, no React. A line is one project; linking is `projects.client_id`, so adding a line sets it to
 * this client and taking one off sets it to none.
 */
import { fmtId } from '../../utils/jiraId';
import { roleLevel } from '../../utils/roles';
import type { ClientProject } from '../../services/ClientService';
import type { PipelineStage, Workspace } from '../../utils/workspaces';

export interface ClientProjectLine {
  /** Stable while editing: the project id for a loaded line, a counter for a new one. */
  key: string;
  /** Empty while a new line has no project chosen yet. */
  projectId: string;
  name: string;
  date: string | null;
  /** Null when the project is not in the signed-in person's project list. */
  stage: PipelineStage | null;
  contractValue: string | null;
  contractCurrency: string | null;
  /** Name of the client the project is with now, when it is not this one (a new line only). */
  movedFrom: string | null;
}

/** The client a project is linked to, as the project list details say. */
export interface ProjectOwner { clientId: string | null; clientName: string | null }

export const projectRef = (projectId: string): string => fmtId('PJ', projectId);

let counter = 0;
export const newDraftLine = (): ClientProjectLine => ({
  key: `new-${++counter}`, projectId: '', name: '', date: null, stage: null, contractValue: null, contractCurrency: null, movedFrom: null,
});

/** One line per project the client has, enriched from the project list when the person can see that project. */
export function linesFromClient(projects: readonly ClientProject[], workspaces: readonly Workspace[]): ClientProjectLine[] {
  return projects.map(p => {
    const w = workspaces.find(x => x.project_id === p.id);
    return {
      key: p.id,
      projectId: p.id,
      name: w?.project_name || p.name,
      date: w?.project_date ?? p.project_date ?? null,
      stage: w?.pipeline_stage ?? null,
      contractValue: w?.contract_value ?? null,
      contractCurrency: w?.contract_currency ?? null,
      movedFrom: null,
    };
  });
}

/** A new line for a chosen project. */
export function lineForProject(key: string, w: Workspace, owner: ProjectOwner | undefined, thisClientId: string | null): ClientProjectLine {
  const other = owner && owner.clientId && owner.clientId !== thisClientId ? (owner.clientName || 'another client') : null;
  return {
    key, projectId: w.project_id, name: w.project_name, date: w.project_date, stage: w.pipeline_stage,
    contractValue: w.contract_value, contractCurrency: w.contract_currency, movedFrom: other,
  };
}

/**
 * Projects a line may pick: live, ones the person can edit (planner or above on that project), not already
 * listed (except the line's own choice), and with no client or another client. `owners` is the client link of every
 * project; a project missing from it is left out, since linking it could silently take it from someone.
 */
export function pickableProjects(
  workspaces: readonly Workspace[], lines: readonly ClientProjectLine[], owners: ReadonlyMap<string, ProjectOwner>, keepForKey?: string,
): Workspace[] {
  const used = new Set(lines.filter(l => l.key !== keepForKey).map(l => l.projectId).filter(Boolean));
  return workspaces.filter(w => !w.archived_at && roleLevel(w.role) >= roleLevel('planner') && !used.has(w.project_id) && owners.has(w.project_id));
}

/** Label of an option in the picker; says so when the project is with another client. */
export function pickerLabel(w: Workspace, owner: ProjectOwner | undefined, thisClientId: string | null): string {
  const base = `${w.project_name} (${projectRef(w.project_id)})`;
  return owner?.clientId && owner.clientId !== thisClientId ? `${base} - with ${owner.clientName || 'another client'}` : base;
}

export interface LinkChanges { add: ClientProjectLine[]; remove: ClientProjectLine[] }

/** Lines to link and lines to unlink, comparing what was loaded with what is on screen (by project). */
export function diffLinks(initial: readonly ClientProjectLine[], now: readonly ClientProjectLine[]): LinkChanges {
  const before = new Set(initial.map(l => l.projectId));
  const after = new Set(now.map(l => l.projectId).filter(Boolean));
  return {
    add: now.filter(l => l.projectId && !before.has(l.projectId)),
    remove: initial.filter(l => !after.has(l.projectId)),
  };
}

export const hasLinkChanges = (c: LinkChanges): boolean => c.add.length > 0 || c.remove.length > 0;

/** One sentence per problem line, by line key: no project chosen, or the same project twice. */
export function validateLinks(lines: readonly ClientProjectLine[]): Record<string, string> {
  const errors: Record<string, string> = {};
  const seen = new Map<string, string>();
  for (const l of lines) {
    if (!l.projectId) { errors[l.key] = 'Choose a project, or remove this line.'; continue; }
    const first = seen.get(l.projectId);
    if (first) { errors[l.key] = 'This project is already listed.'; continue; }
    seen.set(l.projectId, l.key);
  }
  return errors;
}
